import {
  equalIgnoringCaseAndSpaces,
  isLikelyPluralization,
} from './typesConfigUtils';

export type RenamePair = { from: string; to: string };

export function computeRenamePairs(
  prev: string[],
  next: string[]
): RenamePair[] {
  const removed = prev.filter((t) => !next.includes(t));
  const added = next.filter((t) => !prev.includes(t));
  const used = new Set<string>();
  const pairs: RenamePair[] = [];
  for (const from of removed) {
    const to = added.find(
      (a) =>
        !used.has(a) &&
        (equalIgnoringCaseAndSpaces(from, a) ||
          isLikelyPluralization(from, a) ||
          from.toLowerCase() === a.toLowerCase())
    );
    if (to) {
      used.add(to);
      pairs.push({ from, to });
    }
  }
  return pairs;
}

export function applyRenamePairsToNav(
  nav: string[],
  pairs: RenamePair[]
): string[] {
  const mapped = nav.map((t) => pairs.find((p) => p.from === t)?.to ?? t);
  // Dedupe, preserve order
  return Array.from(
    mapped.reduce((acc, cur) => {
      if (!acc.has(cur)) acc.add(cur);
      return acc;
    }, new Set<string>())
  );
}

export function dedupeNormalized(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.replace(/\s+/g, '').toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(v);
    }
  }
  return out;
}

export async function persistRenamesAndBroadcast(params: {
  renamePairs: RenamePair[];
  navTypes: string[];
  newTypes: string[];
  sortMode: 'alpha' | 'manual';
  showAllNav: boolean;
  persistConfig: (args: {
    types: string[];
    navTypes: string[];
    orderMode?: 'ALPHA' | 'MANUAL';
    showAllInNav?: boolean;
    isCustomized?: boolean;
  }) => Promise<unknown>;
  broadcast?: (from: string, to: string) => void;
}): Promise<void> {
  if (params.renamePairs.length === 0) return;
  const newTypesNormalized = dedupeNormalized(params.newTypes);
  const navTypesNext = dedupeNormalized(
    applyRenamePairsToNav(
      params.navTypes.filter((t) => newTypesNormalized.includes(t)),
      params.renamePairs
    )
  );
  await params.persistConfig({
    types: newTypesNormalized,
    orderMode: params.sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
    navTypes: navTypesNext,
    showAllInNav: params.showAllNav,
    isCustomized: navTypesNext.length > 0,
  });
  if (params.broadcast) {
    try {
      for (const { from, to } of params.renamePairs) params.broadcast(from, to);
    } catch {
      /* ignore */
    }
  }
}

export type MultiRenameOutcome = {
  handled: boolean;
  renamePairs: RenamePair[];
  newTypesNormalized: string[];
  navTypesNext: string[];
};

export async function saveMultiRenamesIfAny(params: {
  baseList: string[];
  newTypes: string[];
  sortMode: 'alpha' | 'manual';
  navTypes: string[];
  showAllNav: boolean;
  persistConfig: (args: {
    types: string[];
    navTypes: string[];
    orderMode?: 'ALPHA' | 'MANUAL';
    showAllInNav?: boolean;
    isCustomized?: boolean;
  }) => Promise<unknown>;
  broadcast?: (from: string, to: string) => void;
}): Promise<MultiRenameOutcome> {
  const {
    baseList,
    newTypes,
    sortMode,
    navTypes,
    showAllNav,
    persistConfig,
    broadcast,
  } = params;
  const renamePairs = computeRenamePairs(baseList, newTypes);
  if (renamePairs.length === 0) {
    const newTypesNormalized = dedupeNormalized(newTypes);
    const navTypesMapped = navTypes.filter(
      (t) => t.trim().length > 0 && newTypesNormalized.includes(t)
    );
    const navTypesNext = dedupeNormalized(navTypesMapped);

    return {
      handled: false,
      renamePairs: [],
      newTypesNormalized,
      navTypesNext,
    };
  }

  const newTypesNormalized = dedupeNormalized(newTypes);
  const navTypesMapped = applyRenamePairsToNav(
    navTypes.filter((t) => newTypesNormalized.includes(t)),
    renamePairs
  );
  const navTypesNext = dedupeNormalized(navTypesMapped);

  await persistRenamesAndBroadcast({
    renamePairs,
    navTypes,
    newTypes: newTypesNormalized,
    sortMode,
    showAllNav,
    persistConfig,
    broadcast,
  });

  return { handled: true, renamePairs, newTypesNormalized, navTypesNext };
}

// Shared suppression helpers (DRY)
export function getRenameSuppressMap(
  storageKey: string
): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const data = JSON.parse(raw) as Record<string, number>;
    return data || {};
  } catch {
    return {};
  }
}

export function markRenameSuppressed(
  storageKey: string,
  type: string,
  ttlMs = 5000
): void {
  try {
    const map = getRenameSuppressMap(storageKey);
    map[type] = Date.now() + ttlMs;
    sessionStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function isRenameSuppressed(storageKey: string, type: string): boolean {
  try {
    const map = getRenameSuppressMap(storageKey);
    const until = map[type] || 0;
    const active = until > Date.now();
    if (!active && until) {
      delete map[type];
      sessionStorage.setItem(storageKey, JSON.stringify(map));
    }
    return active;
  } catch {
    return false;
  }
}

export function listenAndSuppressRenames(
  eventName: string,
  storageKey: string,
  ttlMs = 5000
): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as { from?: string } | undefined;
    const from = detail?.from;
    if (typeof from === 'string' && from)
      markRenameSuppressed(storageKey, from, ttlMs);
  };
  window.addEventListener(eventName, handler as EventListener);
  return () => window.removeEventListener(eventName, handler as EventListener);
}

export function filterDeletionCandidates(
  prev: string[],
  next: string[],
  pending: string | string[] | null,
  renamedFrom: string | string[] | null
): string[] {
  const removedNow = prev.filter((t) => !next.includes(t));

  const pendingSet = new Set(
    Array.isArray(pending) ? pending : pending ? [pending] : []
  );
  const renamedSet = new Set(
    Array.isArray(renamedFrom) ? renamedFrom : renamedFrom ? [renamedFrom] : []
  );

  return removedNow
    .filter((t) => !pendingSet.has(t) && !renamedSet.has(t))
    .filter(
      (t) =>
        !next.some(
          (n) =>
            equalIgnoringCaseAndSpaces(t, n) ||
            isLikelyPluralization(t, n) ||
            t.toLowerCase() === n.toLowerCase()
        )
    );
}
